import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useToolTracking } from '@screentone/addon-auth-wrapper';
import { AvatarGroup, IconInfo, Loader, Token, Typography, Wrapper } from '@screentone/core';

import { SummarianWarning } from 'components/altsumm-modal/SummarianWarning';
import Countdown from 'components/countdown/Countdown';
import PageLock from 'components/page-lock/PageLock';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { usePageLock } from 'contexts/page-lock/usePageLock';
import { usePagePublish } from 'contexts/page-publish-dto/usePagePublish';
import { AvailableFeatureFlags, SectionContainer } from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { formatInTimeZone } from 'utils/dates';
import { safelyParseContent } from 'utils/temp';
import { PagePublishingButtons } from './components/page-publishing-buttons/PagePublishingButtons';
import styles from './PageTitleHeader.module.scss';

export const PageTitleHeader = () => {
  const { hasRecentlyPublished, soonToBePublishedScheduledContentPublishUtc } = usePagePublish();
  const { data: publicationSettings } = usePublicationSettings();
  const { metadata, getEmbargoContent } = useDataModelContext<SectionContainer>();
  const { state, lockPageFragment, releasePageFragment, loading, refetcher } = usePageLock();
  const autolockAttemptedRef = useRef(false);
  const [lockIsReady, setLockIsReady] = useState(false);
  const publishingFragment = state.publishing;
  const isPageLockingEnabled = useFeatureFlag(AvailableFeatureFlags.PageLockingEnabled, false);

  const handleLock = useCallback(
    async (withForce: boolean) => {
      try {
        const result = await lockPageFragment('publishing', withForce);

        // withForce = true means to take over, not claim.
        // For take over we need to wait until the message for the update arrives in SectionPageEditPage.tsx
        if (!withForce) {
          refetcher?.();
        }

        return result;
      } catch (error) {
        console.error('Error acquiring page lock:', error);
      }

      return null;
    },
    [lockPageFragment, refetcher]
  );

  const handleRelease = useCallback(
    async (withForce: boolean) => {
      try {
        const result = await releasePageFragment('publishing', withForce);

        refetcher?.();

        return result;
      } catch (error) {
        console.error('Error releasing page lock:', error);
      }

      return null;
    },
    [releasePageFragment, refetcher]
  );

  useEffect(() => {
    if (!loading && !autolockAttemptedRef.current) {
      autolockAttemptedRef.current = true;

      // Attempt to acquire lock on mount
      lockPageFragment('publishing')
        .finally(() => {
          setLockIsReady(true);
        })
        .catch(console.error);
    }
  }, [loading, lockPageFragment]);

  const filters = React.useMemo(() => {
    if (!publicationSettings) return {};

    const [slug] =
      Object.entries(publicationSettings.publicationSetting.slugMappings ?? {}).find(
        ([, groupKey]) => groupKey === metadata.allessehId
      ) ?? [];

    const base = `^/${publicationSettings.publicationSetting.publicationKey}/pages/`;

    if (slug) {
      return {
        path: new RegExp(`${base}(${metadata.allessehId}|${slug})$`, 'gi')
      };
    }

    return {
      path: new RegExp(`${base}${metadata.allessehId}$`, 'gi')
    };
  }, [metadata.allessehId, publicationSettings]);

  const { loading: userTrackingLoading, data: userTrackingData } = useToolTracking(filters);

  const latestEmbargoContent = getEmbargoContent()
    .sort((a, b) => {
      const aDateTime = safelyParseContent<AllessehContent>(a).data.attributes.embargo_datetime_utc;
      const bDateTime = safelyParseContent<AllessehContent>(b).data.attributes.embargo_datetime_utc;

      const aDate = new Date(aDateTime!);
      const bDate = new Date(bDateTime!);

      return aDate < bDate ? 1 : -1;
    })
    .at(0);
  let latestEmbargoDateTime: string | undefined;
  if (latestEmbargoContent) {
    latestEmbargoDateTime = formatInTimeZone(
      safelyParseContent<AllessehContent>(latestEmbargoContent).data.attributes.embargo_datetime_utc!,
      publicationSettings?.publicationSetting.preferredTimezone,
      "M/dd/yyyy 'at' hh:mm aaaa",
      { showTimezoneAbbreviation: true }
    );
  }

  return (
    <Wrapper padding={{ top: 'md', bottom: 'md' }} className={styles.titleHeader}>
      <div className={styles.row}>
        <Typography variant="header2" margin={{ bottom: 'none' }} data-testid="page-name-title">
          {metadata.name}
        </Typography>
        {publishingFragment.isReadOnly && lockIsReady && (
          <Token color="gray" data-testid="read-only-label">
            Read Only
          </Token>
        )}
        {hasRecentlyPublished && (
          <Typography color="asphalt" margin={{ left: 'md' }} data-testid="page-status-label">
            Published
          </Typography>
        )}
      </div>
      <div className={styles.row}>
        <SummarianWarning />
        {soonToBePublishedScheduledContentPublishUtc && (
          <Countdown
            timeInMilliseconds={soonToBePublishedScheduledContentPublishUtc}
            timerMessage="Next Scheduled Publish in"
            atZeroMessage="Scheduled Publish should be happening any moment now..."
          />
        )}
        {latestEmbargoContent && latestEmbargoDateTime && (
          <div className={styles.embargoWarning}>
            <IconInfo />
            <Typography margin={{ right: 'xs' }} data-testid="page-embargo-date-label">
              Embargoed story for {latestEmbargoDateTime}
            </Typography>
          </div>
        )}
        {lockIsReady && isPageLockingEnabled && (
          <PageLock
            publishingState={state.publishing}
            release={handleRelease}
            lock={handleLock}
            loading={loading}
            lockType="Page"
            draftEnabled
          />
        )}
        {userTrackingData.length > 0 && (
          <AvatarGroup>
            {!userTrackingLoading &&
              userTrackingData.map((user) => (
                <AvatarGroup.Item
                  initials={
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    user.data.user.name.charAt(0) ??
                    user.data.user.email.charAt(0) ??
                    user.data.user.dj_user_id?.charAt(0)
                  }
                  displayName={user.data.user.name}
                  email={user.data.user.email}
                  avatar={user.data.user.picture}
                  key={`${user.data.user.name}-${user.data.user.email}-${user.data.user.dj_user_id}`}
                />
              ))}
          </AvatarGroup>
        )}
        {!publishingFragment.isReadOnly && lockIsReady && <PagePublishingButtons />}
        {!lockIsReady && <Loader size="md" />}
      </div>
    </Wrapper>
  );
};
